import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function DropdownMenu({ buttonRef, onClose, children, dropdownWidth = 160, dropdownHeight = 120, align = "left" }) {
  const dropdownRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // Decide vertical position
      let top;
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        top = rect.top - dropdownHeight;
      } else {
        top = rect.bottom;
      }

      // Horizontal position based on alignment
      const padding = 8; // px gap from screen edge
      let left;
      
      if (align === "right") {
        // Align dropdown's right edge with button's right edge
        left = rect.right - dropdownWidth;
      } else {
        // Default: align dropdown's left edge with button's left edge
        left = rect.left;
      }
      
      // Clamp horizontal position to stay within viewport
      const maxLeft = window.innerWidth - dropdownWidth - padding;
      if (left > maxLeft) left = maxLeft;
      if (left < padding) left = padding;

      setPosition({ top, left });
    }
  }, [buttonRef, dropdownWidth, dropdownHeight, align]);

  // Recalculate position on window resize
  useEffect(() => {
    const handleResize = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        let top;
        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
          top = rect.top - dropdownHeight;
        } else {
          top = rect.bottom;
        }

        const padding = 8;
        let left;
        
        if (align === "right") {
          left = rect.right - dropdownWidth;
        } else {
          left = rect.left;
        }
        
        const maxLeft = window.innerWidth - dropdownWidth - padding;
        if (left > maxLeft) left = maxLeft;
        if (left < padding) left = padding;

        setPosition({ top, left });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [buttonRef, dropdownWidth, dropdownHeight, align]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose, buttonRef]);

  return createPortal(
    <div
      ref={dropdownRef}
      className="absolute bg-white dark:bg-gray-800 shadow-lg rounded-lg border z-[9999] overflow-hidden"
      style={{
        top: position.top,
        left: position.left,
        width: dropdownWidth,
        position: "fixed",
      }}>
      {children}
    </div>,
    document.body
  );
}
